/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Component imports
import { Button, Col, Form, Input, Row, Typography } from 'antd';
import RichTextEditor from '../../../Forms/Fields/RichTextEditor/RichTextEditor';
import { CodeIcon } from '../../../Icons/CodeIcon';
import { DeleteIcon } from '../../../Icons/DeleteIcon';
import { PlusIcon } from '../../../Icons/PlusIcon';

// Misc imports
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';
import { ACTIVITY_TYPES, VARIABLE_TYPES } from '../../../../constants/main';
import { useParams, useSearchParams } from 'react-router-dom';
import { getCurrentQueryState } from '../../../../store/utils/getCurrentQueryState';

// Constant declarations
const { Text } = Typography;

const MultiOptionNodeForm = ({ form, selectedElement, setCanSubmit }) => {
  const { t } = useTranslation();
  const { currentBotLanguage, primaryBotLanguage, isEditingPrimaryLanguage } = useBotLanguage();
  const { orgId, tenantId } = useParams();
  const [searchParams] = useSearchParams();

  const { data: activity } = getCurrentQueryState('fetchActivity', { orgId, tenantId, activityId: searchParams.get('activity'), currentBotLanguage }, !searchParams.get('activity'));

  const [openRichTextOptions, setOpenRichTextOptions] = useState([]);

  useEffect(() => {
    // If there are no fields, add fields identical to the primary language
    if (!selectedElement.data.multiOptions[currentBotLanguage]) {
      form.setFieldsValue({
        data: {
          multiOptions: {
            [currentBotLanguage]: Object.entries(selectedElement.data.multiOptions[primaryBotLanguage] || {}).map(([key, value]) => {
              return ({ key: key, value: value });
            })
          }
        }
      });

      // Notify form of change to enable the submit button
      form.validateFields();
    }
  }, [currentBotLanguage, primaryBotLanguage, selectedElement]);

  return (
    <Fragment>
      <Row gutter={16} wrap={false} className="mb-1">
        <Col flex="auto">
          <Row gutter={16}>
            <Col xs={12}>
              <Text>
                {t('Option key')}
              </Text>
              <br/>
              <Text type="secondary">
                {t('This will be the text the end user will see.')}
                <br/>
                {t('(eg. English, German, French, etc.)')}
              </Text>
            </Col>
            <Col xs={12}>
              <Text>
                {t('Option value')}
              </Text>
              <br/>
              <Text type="secondary">
                {t('This is the underlying value of each key.')}
                <br/>
                {t('(eg. en, de, fr, etc.)')}
              </Text>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row gutter={16} className="mt-4">
        <Col xs={24}>
          <Form.List
            name={['data', 'multiOptions', currentBotLanguage]}
          >
            {(fields, { add, remove }, { errors }) => (
              <Fragment>
                <div className="mb-3">
                  {fields.map(({ key, name, ...restField }, _index) => (
                    <Form.Item
                      key={key}
                      className="mb-4"
                    >
                      <Row wrap={false} gutter={8} align="bottom">
                        <Col flex="auto">
                          <Row gutter={16}>
                            <Col className="d-flex flex-column justify-content-end" sm={12} xs={24}>
                              <Form.Item
                                {...restField}
                                name={[name, 'key']}
                                rules={[{ required: true }]}
                                className="mb-0 hide-error"
                              >
                                <Input
                                  placeholder={t('Option key')}
                                  size="large"
                                />
                              </Form.Item>
                            </Col>

                            <Col sm={12} xs={24}>
                              <Form.Item
                                {...restField}
                                name={[name, 'value']}
                                rules={[{ required: true }]}
                                className="mb-0 hide-error"
                              >
                                <RichTextEditor
                                  placeholder={t('Option value')}
                                  index={name}
                                  inline={true}
                                  className="fs-base"
                                  disabled={!isEditingPrimaryLanguage}
                                  isToolbarOpen={openRichTextOptions.includes(key)}
                                  onChange={(newValue, _delta, _source, _editor, originalValue) => {
                                    // If the value of the primary language changed, update the value of the OTHER languages (do not touch the keys)
                                    if (currentBotLanguage === primaryBotLanguage) {
                                      let newMultiOptions = form.getFieldValue(['data', 'multiOptions']);

                                      // For each other language, check if the old value existed so we can update it
                                      Object.entries(newMultiOptions).forEach(([lang, objArray]) => {
                                        if (lang !== primaryBotLanguage) {
                                          const found = objArray.find(obj => obj.value === originalValue);
                                          if (found) {
                                            newMultiOptions[lang] = objArray.map(obj => obj.value === originalValue ? { key: obj.key, value: newValue } : obj);
                                          }
                                        }
                                      });

                                      form.setFieldsValue({
                                        data: { multiOptions: newMultiOptions }
                                      });
                                    }
                                  }}
                                  toggleButton={
                                    <Button
                                      className="position-absolute right-0 top-0 mr-2"
                                      style={{ marginTop: '2px' }}
                                      disabled={!isEditingPrimaryLanguage}
                                      type="link"
                                      icon={<CodeIcon />}
                                      onClick={() => {
                                        if (openRichTextOptions.includes(key)) {
                                          setOpenRichTextOptions(openRichTextOptions.filter(option => option !== key));
                                        } else {
                                          setOpenRichTextOptions([...openRichTextOptions, key]);
                                        }
                                      }}
                                    />
                                  }
                                  includedVariableTypes={activity?.type === ACTIVITY_TYPES.LIVECHAT_FLOW ? [VARIABLE_TYPES.LIVECHAT] : []}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Col>

                        {
                          fields.length > 1 ? (
                            <Col flex="none">
                              <Button
                                icon={<DeleteIcon />}
                                size="large"
                                type="text"
                                onClick={() => remove(name)}
                                disabled={!isEditingPrimaryLanguage}
                              />
                            </Col>
                          ) : null
                        }
                      </Row>
                    </Form.Item>
                  ))}
                </div>

                <Row gutter={16}>
                  <Col sm={12} xs={24}>
                    <Button
                      type="dashed"
                      size="large"
                      block
                      disabled={!isEditingPrimaryLanguage}
                      onClick={() => {
                        add();

                        // Do not allow form submission if there are empty values
                        setCanSubmit(false);
                      }}
                      icon={<PlusIcon />}
                    >
                      {t('Add new option')}
                    </Button>
                  </Col>
                </Row>

                <Form.ErrorList errors={errors} />
              </Fragment>
            )}
          </Form.List>
        </Col>
      </Row>
    </Fragment>
  );
};

MultiOptionNodeForm.propTypes = {
  form: PropTypes.object,
  selectedElement: PropTypes.object,
  setCanSubmit: PropTypes.func.isRequired
};

export default MultiOptionNodeForm;
